import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth.jsx';
import { useTeam, useTeamMutations } from '../../../hooks/useSettings.js';
import { ApiError } from '../../../lib/api.js';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog.jsx';
import { formatRelative } from '../../../lib/dates.js';

/**
 * TeamPage — manage admins for this tenant.
 *
 * Shows two sections:
 *   1. Active admins (with role pills, last-login, remove button)
 *   2. Pending invitations (with revoke button)
 *
 * Invite form at the top. Owner role = full control (including
 * billing + deletion). Member role = everything except billing + team
 * management + deletion.
 *
 * The current user's own row shows a "you" pill and their remove
 * button is disabled — can't remove yourself.
 */

export function TeamPage() {
  const { admin: me } = useAuth();
  const { admins, invitations, loading, error, refresh } = useTeam();
  const { inviteAdmin, removeAdmin, revokeInvitation } = useTeamMutations();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const [removeAdmin_, setRemoveAdmin_] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [revokeInvite_, setRevokeInvite_] = useState(null);
  const [revoking, setRevoking] = useState(false);

  async function handleInvite(e) {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(false);

    if (!inviteEmail) {
      setInviteError('Enter an email address.');
      return;
    }

    setInviting(true);
    try {
      await inviteAdmin(inviteEmail.trim().toLowerCase(), inviteRole);
      setInviteEmail('');
      setInviteSuccess(true);
      refresh();
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : 'Could not send invitation.');
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove() {
    if (!removeAdmin_) return;
    setRemoving(true);
    try {
      await removeAdmin(removeAdmin_.id);
      setRemoveAdmin_(null);
      refresh();
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : 'Could not remove admin.');
    } finally {
      setRemoving(false);
    }
  }

  async function handleRevoke() {
    if (!revokeInvite_) return;
    setRevoking(true);
    try {
      await revokeInvitation(revokeInvite_.id);
      setRevokeInvite_(null);
      refresh();
    } catch (err) {
      setInviteError(err instanceof ApiError ? err.message : 'Could not revoke invitation.');
    } finally {
      setRevoking(false);
    }
  }

  if (loading) return <Loading />;
  if (error) return <LoadError onRetry={refresh} />;

  return (
    <>
      {inviteError && <div className="alert alert-error" role="alert">{inviteError}</div>}
      {inviteSuccess && (
        <div className="alert alert-success" role="status">
          Invitation sent. They'll receive an email with a link to join your organisation.
        </div>
      )}

      {/* Invite form */}
      <form onSubmit={handleInvite} className="settings-card">
        <h2 className="settings-card-title">Invite a team member</h2>
        <p className="settings-card-body">
          Add another admin to help manage your staff, scans, and
          branding. They'll receive an email invitation that expires
          after 7 days.
        </p>

        <div className="field-row" style={{ gridTemplateColumns: '1fr 180px auto', alignItems: 'end' }}>
          <div className="field">
            <label className="field-label" htmlFor="f-invite-email">Email</label>
            <input
              id="f-invite-email"
              className="field-input"
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="colleague@yourorganisation.co.uk"
              required
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="f-invite-role">Role</label>
            <select
              id="f-invite-role"
              className="field-select"
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
            >
              <option value="member">Member</option>
              <option value="owner">Owner</option>
            </select>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={inviting}
            aria-busy={inviting}
            style={{ height: 44 }}
          >
            {inviting ? (<><span className="spinner" aria-hidden="true" /> Sending…</>) : 'Send invite'}
          </button>
        </div>

        <p className="field-hint" style={{ marginTop: 'var(--sp-2)' }}>
          <strong>Members</strong> can manage staff, branding, and
          scans. <strong>Owners</strong> can also manage billing, team,
          and delete the organisation.
        </p>
      </form>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="settings-card" style={{ padding: 0, overflow: 'hidden' }}>
          <h2 className="settings-card-title" style={{ padding: 'var(--sp-5) var(--sp-5) 0' }}>
            Pending invitations
          </h2>
          <p className="settings-card-body" style={{ padding: '0 var(--sp-5) var(--sp-3)' }}>
            Invitations expire after 7 days if not accepted.
          </p>

          {invitations.map(inv => (
            <div key={inv.id} className="invitation-row">
              <div className="team-avatar">
                {(inv.email.charAt(0) || '?').toUpperCase()}
              </div>
              <div className="team-info">
                <div className="team-email">{inv.email}</div>
                <div className="team-meta">
                  Invited {formatRelative(inv.invited_at)}
                  {inv.invited_by_email && <> by {inv.invited_by_email}</>}
                </div>
              </div>
              <span className={`role-pill ${inv.role === 'owner' ? 'owner' : ''}`}>
                {inv.role}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: 'var(--id-red-d)', fontSize: 'var(--fs-sm)', padding: '0.4rem 0.8rem' }}
                onClick={() => setRevokeInvite_(inv)}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Active admins */}
      <div className="settings-card" style={{ padding: 0, overflow: 'hidden' }}>
        <h2 className="settings-card-title" style={{ padding: 'var(--sp-5) var(--sp-5) 0' }}>
          Team ({admins.length})
        </h2>

        {admins.map(a => {
          const isMe = a.id === me?.id || a.email === me?.email;
          return (
            <div key={a.id} className="team-row">
              <div className="team-avatar">
                {(a.email.charAt(0) || '?').toUpperCase()}
              </div>
              <div className="team-info">
                <div className="team-email">
                  {a.email}
                  {isMe && <span className="you-pill">You</span>}
                </div>
                <div className="team-meta">
                  {a.last_login_at
                    ? `Last login ${formatRelative(a.last_login_at)}`
                    : 'Never logged in'}
                </div>
              </div>
              <span className={`role-pill ${a.role === 'owner' ? 'owner' : ''}`}>
                {a.role}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: 'var(--id-red-d)', fontSize: 'var(--fs-sm)', padding: '0.4rem 0.8rem' }}
                onClick={() => setRemoveAdmin_(a)}
                disabled={isMe}
                title={isMe ? "You can't remove yourself" : undefined}
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!removeAdmin_}
        title="Remove this admin?"
        body={
          removeAdmin_ && (
            <>
              <strong>{removeAdmin_.email}</strong> will lose access to
              this organisation immediately. Their scan history stays for audit.
            </>
          )
        }
        confirmLabel="Remove admin"
        destructive
        submitting={removing}
        onConfirm={handleRemove}
        onCancel={() => setRemoveAdmin_(null)}
      />

      <ConfirmDialog
        open={!!revokeInvite_}
        title="Revoke invitation?"
        body={
          revokeInvite_ && (
            <>
              The invite link sent to <strong>{revokeInvite_.email}</strong> will
              stop working immediately.
            </>
          )
        }
        confirmLabel="Revoke invitation"
        destructive
        submitting={revoking}
        onConfirm={handleRevoke}
        onCancel={() => setRevokeInvite_(null)}
      />
    </>
  );
}

function Loading() {
  return (
    <div style={{ padding: 'var(--sp-10) 0', textAlign: 'center' }}>
      <div className="spinner" aria-hidden="true" />
    </div>
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="alert alert-error" role="alert">
      <strong>Couldn't load team.</strong>{' '}
      <button type="button" className="btn btn-ghost" onClick={onRetry} style={{ padding: 0, textDecoration: 'underline' }}>
        Try again
      </button>
    </div>
  );
}
